import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { SITE, canonical } from "../data/siteConfig";

/**
 * Setzt pro Seite: title, meta description, canonical, OG, Twitter, JSON-LD.
 * Keine zusätzliche Dependency – nutzt document und useEffect.
 */
const SEO = ({
  title,
  description,
  path = null,
  noIndex = false,
  ogImage = null,
  jsonLd = null,
  structuredData = null,
}) => {
  const location = useLocation();
  const pathname = path !== null ? path : location.pathname;
  const canonicalUrl = canonical(pathname);
  const metaDesc = description || SITE.defaultDescription;
  const fullTitle = title ? `${title} | ${SITE.name}` : SITE.defaultTitle;
  const imageUrl = ogImage ? (ogImage.startsWith("http") ? ogImage : `${SITE.url}${ogImage}`) : `${SITE.url}/logo.svg`;
  const scriptIdRef = useRef(null);

  useEffect(() => {
    document.title = fullTitle;

    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement("meta");
      metaDescription.setAttribute("name", "description");
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute("content", metaDesc);

    let linkCanonical = document.querySelector('link[rel="canonical"]');
    if (!linkCanonical) {
      linkCanonical = document.createElement("link");
      linkCanonical.setAttribute("rel", "canonical");
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute("href", canonicalUrl);

    const setMeta = (attr, key, value) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", metaDesc);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", imageUrl);
    setMeta("property", "og:locale", SITE.locale);
    setMeta("property", "og:type", "website");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", metaDesc);
    setMeta("name", "twitter:image", imageUrl);

    if (noIndex) {
      let robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex, nofollow");
    }

    const dataToInject = structuredData || jsonLd;
    if (dataToInject) {
      const existing = document.getElementById("seo-json-ld");
      if (existing) existing.remove();
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.text = JSON.stringify(Array.isArray(dataToInject) ? dataToInject : dataToInject);
      script.id = "seo-json-ld";
      scriptIdRef.current = "seo-json-ld";
      document.head.appendChild(script);
    }

    return () => {
      const existing = document.getElementById(scriptIdRef.current);
      if (existing) existing.remove();
    };
  }, [fullTitle, metaDesc, canonicalUrl, imageUrl, noIndex, structuredData, jsonLd]);

  return null;
};

export default SEO;
